import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { getReferralsByUsername, getAllUsersFromStorage } from '../../../../utils/sharedStorage';
import type { User } from '../../../../utils/sharedStorage';

const REFERRAL_COMMISSIONS = { level1: 1500, level2: 600, level3: 400 };

interface FilleulRow {
  name: string;
  username: string;
  phone: string;
  country: string;
  level: 1 | 2 | 3;
  isActivated: boolean;
  registrationDate: string;
  activatedAt?: string;
  commission: number;
}

interface LevelStats {
  total: number;
  activated: number;
  pending: number;
  totalCommission: number;
}

export default function FilleulsTableau() {
  const [filleuls, setFilleuls] = useState<FilleulRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeLevel, setActiveLevel] = useState<'all' | 1 | 2 | 3>('all');
  const [activeStatus, setActiveStatus] = useState<'all' | 'activated' | 'pending'>('all');
  const [stats, setStats] = useState<Record<1 | 2 | 3, LevelStats>>({
    1: { total: 0, activated: 0, pending: 0, totalCommission: 0 },
    2: { total: 0, activated: 0, pending: 0, totalCommission: 0 },
    3: { total: 0, activated: 0, pending: 0, totalCommission: 0 },
  });
  const [currency, setCurrency] = useState('FCFA');
  const [myUsername, setMyUsername] = useState('');
  const [myReferralCode, setMyReferralCode] = useState('');

  useEffect(() => {
    loadFilleuls();
    
    // Abonnement Supabase Realtime pour les nouveaux filleuls
    if (supabase) {
      const user = localStorage.getItem('currentUser');
      if (user) {
        const parsed = JSON.parse(user);
        const username = parsed.username || parsed.name || '';
        const referralCode = parsed.referralCode || username;
        
        if (username || referralCode) {
          const channel = supabase
            .channel('referrals_updates')
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'users',
                filter: `site_id=eq.SUCCESSLINK_AGENCIA`,
              },
              (payload) => {
                console.log('Mise à jour filleuls détectée:', payload);
                loadFilleuls(true);
              }
            )
            .subscribe();

          return () => {
            supabase.removeChannel(channel);
          };
        }
      }
    }
    
    const interval = setInterval(() => loadFilleuls(true), 15000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Cherche les filleuls d'un utilisateur depuis Supabase en priorité
   */
  const getFilleulsFor = async (identifier: string, name?: string): Promise<User[]> => {
    if (!identifier && !name) return [];
    const results = new Map<string, User>();

    // Chercher dans Supabase en priorité
    if (supabase && identifier) {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('site_id', 'SUCCESSLINK_AGENCIA')
          .or(`referred_by.eq.${identifier},referred_by.ilike.${identifier}`);

        if (!error && data) {
          data.forEach((u: any) => {
            results.set(u.phone, {
              phone: u.phone,
              name: u.name,
              username: u.username,
              referralCode: u.referral_code,
              referredBy: u.referred_by,
              isActivated: u.is_activated,
              activated: u.is_activated,
              accountStatus: u.is_activated ? 'active' : 'pending',
              activatedAt: u.activated_at,
              registrationDate: u.created_at,
              country: u.country,
              password: '',
              balance: u.balance || 0,
            });
          });
        }
      } catch (err) {
        console.error('Erreur recherche filleuls Supabase:', err);
      }
    }

    // Fallback localStorage
    if (identifier) {
      const r1 = await getReferralsByUsername(identifier);
      r1.forEach(u => {
        if (!results.has(u.phone)) results.set(u.phone, u);
      });
    }

    if (name) {
      const nameLower = name.toLowerCase().replace(/\s+/g, '');
      const allUsers = getAllUsersFromStorage();
      allUsers.forEach(u => {
        if (!u.referredBy) return;
        const ref = String(u.referredBy).toLowerCase().replace(/\s+/g, '');
        if (ref === nameLower && !results.has(u.phone)) results.set(u.phone, u);
      });
    }

    return Array.from(results.values());
  };

  const loadFilleuls = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const user = localStorage.getItem('currentUser');
      if (!user) return;
      const parsed = JSON.parse(user);
      const username = parsed.username || parsed.name || '';
      const referralCode = parsed.referralCode || username;
      const name = parsed.name || '';
      const country = parsed.country || 'CI';

      const COUNTRY_CURRENCY: Record<string, string> = {
        CI: 'FCFA', SN: 'FCFA', BJ: 'FCFA', ML: 'FCFA', TG: 'FCFA',
        BF: 'FCFA', GA: 'FCFA', CM: 'FCFA', CG: 'FCFA', CD: 'FC', GH: 'GH₵',
      };
      setCurrency(COUNTRY_CURRENCY[country] || 'FCFA');
      setMyUsername(username);
      setMyReferralCode(referralCode);

      if (!username && !referralCode) return;

      const rows: FilleulRow[] = [];

      // Niveau 1 : filleuls directs
      const level1: User[] = await getFilleulsFor(username, name);

      // Dédupliquer aussi par referralCode si différent
      if (referralCode && referralCode !== username) {
        const extra = await getFilleulsFor(referralCode);
        extra.forEach(u => {
          if (!level1.find(f => f.phone === u.phone)) level1.push(u);
        });
      }

      for (const f of level1) {
        rows.push({
          name: f.name,
          username: f.username,
          phone: f.phone,
          country: f.country,
          level: 1,
          isActivated: f.isActivated || f.activated === true || f.accountStatus === 'active',
          registrationDate: f.registrationDate,
          activatedAt: f.activatedAt,
          commission: REFERRAL_COMMISSIONS.level1,
        });
      }

      // Niveau 2
      for (const filleul of level1) {
        const filleulId = filleul.username || filleul.referralCode || filleul.name;
        if (!filleulId) continue;
        try {
          const level2: User[] = await getFilleulsFor(filleulId, filleul.name);
          for (const f2 of level2) {
            if (rows.find(r => r.phone === f2.phone)) continue; // éviter doublons
            rows.push({
              name: f2.name,
              username: f2.username,
              phone: f2.phone,
              country: f2.country,
              level: 2,
              isActivated: f2.isActivated || f2.activated === true || f2.accountStatus === 'active',
              registrationDate: f2.registrationDate,
              activatedAt: f2.activatedAt,
              commission: REFERRAL_COMMISSIONS.level2,
            });

            // Niveau 3
            const level2Id = f2.username || f2.referralCode || f2.name;
            if (level2Id) {
              try {
                const level3: User[] = await getFilleulsFor(level2Id, f2.name);
                for (const f3 of level3) {
                  if (rows.find(r => r.phone === f3.phone)) continue;
                  rows.push({
                    name: f3.name,
                    username: f3.username,
                    phone: f3.phone,
                    country: f3.country,
                    level: 3,
                    isActivated: f3.isActivated || f3.activated === true || f3.accountStatus === 'active',
                    registrationDate: f3.registrationDate,
                    activatedAt: f3.activatedAt,
                    commission: REFERRAL_COMMISSIONS.level3,
                  });
                }
              } catch { /* continuer */ }
            }
          }
        } catch { /* continuer */ }
      }

      setFilleuls(rows);

      const newStats: Record<1 | 2 | 3, LevelStats> = {
        1: { total: 0, activated: 0, pending: 0, totalCommission: 0 },
        2: { total: 0, activated: 0, pending: 0, totalCommission: 0 },
        3: { total: 0, activated: 0, pending: 0, totalCommission: 0 },
      };
      for (const row of rows) {
        const lvl = row.level;
        newStats[lvl].total += 1;
        if (row.isActivated) {
          newStats[lvl].activated += 1;
          newStats[lvl].totalCommission += row.commission;
        } else {
          newStats[lvl].pending += 1;
        }
      }
      setStats(newStats);
    } catch (err) {
      console.error('Erreur chargement filleuls:', err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString('fr-FR'); } catch { return dateStr; }
  };

  const filteredFilleuls = filleuls.filter((f) => {
    const levelOk = activeLevel === 'all' || f.level === activeLevel;
    const statusOk =
      activeStatus === 'all' ||
      (activeStatus === 'activated' && f.isActivated) ||
      (activeStatus === 'pending' && !f.isActivated);
    return levelOk && statusOk;
  });

  const totalCommissionGagnee = filleuls
    .filter((f) => f.isActivated)
    .reduce((sum, f) => sum + f.commission, 0);

  const levelColors: Record<number, { bg: string; text: string; badge: string; border: string }> = {
    1: { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-500 text-white', border: 'border-emerald-200' },
    2: { bg: 'bg-teal-50', text: 'text-teal-700', badge: 'bg-teal-500 text-white', border: 'border-teal-200' },
    3: { bg: 'bg-cyan-50', text: 'text-cyan-700', badge: 'bg-cyan-500 text-white', border: 'border-cyan-200' },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-extrabold text-gray-800 flex items-center gap-2">
            <i className="ri-group-line text-teal-500 text-2xl"></i>
            Mon équipe / Mes filleuls
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Code de parrainage : <strong className="text-teal-600">{myReferralCode || myUsername || '—'}</strong>
          </p>
        </div>
        <button
          onClick={() => loadFilleuls()}
          disabled={isLoading}
          className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow"
        >
          <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {/* Cartes récapitulatives par niveau */}
      <div className="grid grid-cols-3 gap-4">
        {([1, 2, 3] as const).map((lvl) => {
          const s = stats[lvl];
          const c = levelColors[lvl];
          return (
            <div key={lvl} className={`rounded-2xl border-2 ${c.border} ${c.bg} p-5 shadow-sm`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-extrabold ${c.badge}`}>
                  Niveau {lvl}
                </span>
                <span className={`text-2xl font-extrabold ${c.text}`}>{s.total}</span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                    Activés
                  </span>
                  <span className="font-bold text-green-600">{s.activated}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span>
                    En attente
                  </span>
                  <span className="font-bold text-yellow-600">{s.pending}</span>
                </div>
                <div className="border-t border-gray-200 pt-1.5 mt-1.5 flex items-center justify-between text-xs">
                  <span className={`font-semibold ${c.text}`}>Commissions</span>
                  <span className={`font-extrabold ${c.text}`}>
                    {s.totalCommission.toLocaleString()} {currency}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bandeau total commissions */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-5 text-white shadow-lg flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full">
            <i className="ri-money-dollar-circle-line text-3xl"></i>
          </div>
          <div>
            <p className="text-teal-100 text-xs font-semibold uppercase tracking-wide">Total commissions gagnées</p>
            <p className="text-3xl font-extrabold">
              {totalCommissionGagnee.toLocaleString()}
              <span className="text-base font-normal ml-1">{currency}</span>
            </p>
            <p className="text-teal-200 text-xs mt-0.5">
              {filleuls.filter(f => f.isActivated).length} filleul(s) activé(s) sur {filleuls.length} au total
            </p>
          </div>
        </div>
        <div className="text-right text-xs text-teal-100 space-y-1">
          <p>N1 : {REFERRAL_COMMISSIONS.level1.toLocaleString()} {currency}/filleul</p>
          <p>N2 : {REFERRAL_COMMISSIONS.level2.toLocaleString()} {currency}/filleul</p>
          <p>N3 : {REFERRAL_COMMISSIONS.level3.toLocaleString()} {currency}/filleul</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-gray-500 mr-1">Niveau :</span>
            {(['all', 1, 2, 3] as const).map((lvl) => (
              <button
                key={lvl}
                onClick={() => setActiveLevel(lvl)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition cursor-pointer whitespace-nowrap ${
                  activeLevel === lvl
                    ? 'bg-teal-500 text-white shadow'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {lvl === 'all' ? 'Tous' : `Niveau ${lvl}`}
              </button>
            ))}
          </div>
          <div className="w-px h-5 bg-gray-200"></div>
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-gray-500 mr-1">Statut :</span>
            {([
              { key: 'all', label: 'Tous' },
              { key: 'activated', label: '✅ Activés' },
              { key: 'pending', label: '⏳ En attente' },
            ] as const).map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveStatus(s.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition cursor-pointer whitespace-nowrap ${
                  activeStatus === s.key
                    ? 'bg-teal-500 text-white shadow'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <span className="ml-auto text-xs text-gray-400 font-medium">
            {filteredFilleuls.length} résultat(s)
          </span>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
            <p className="text-gray-500 text-sm mt-3 font-medium">Chargement de votre équipe...</p>
          </div>
        ) : filteredFilleuls.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-user-search-line text-4xl text-gray-400"></i>
            </div>
            <p className="text-gray-700 font-bold text-base mb-1">
              {filleuls.length === 0 ? 'Aucun filleul pour le moment' : 'Aucun résultat pour ce filtre'}
            </p>
            <p className="text-gray-400 text-sm max-w-sm mx-auto mb-3">
              {filleuls.length === 0
                ? "Partagez votre code d'invitation pour commencer à parrainer !"
                : 'Essayez de modifier les filtres ci-dessus.'}
            </p>
            {filleuls.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-sm mx-auto text-left">
                <p className="text-xs text-amber-800 font-bold mb-1">ℹ️ Comment ça marche ?</p>
                <p className="text-xs text-amber-700">
                  Vos filleuls doivent s'inscrire avec votre code <strong className="text-amber-900">{myReferralCode || myUsername}</strong> pour apparaître ici.
                  Les filleuls s'affichent uniquement sur l'appareil où ils se sont inscrits ou sur le même navigateur.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">#</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Nom</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Niveau</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Statut</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Inscription</th>
                  <th className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Activation</th>
                  <th className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wide">Commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredFilleuls.map((f, idx) => {
                  const c = levelColors[f.level];
                  return (
                    <tr key={`${f.phone}-${f.level}-${idx}`} className="hover:bg-gray-50/60 transition">
                      <td className="px-5 py-4 text-sm text-gray-400 font-medium">{idx + 1}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 flex items-center justify-center rounded-full font-extrabold text-sm ${c.bg} ${c.text} border ${c.border}`}>
                            {(f.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-800">{f.name}</p>
                            <p className="text-xs text-gray-400">@{f.username || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-extrabold ${c.badge}`}>
                          Niveau {f.level}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {f.isActivated ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                            Activé
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 inline-block"></span>
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">{formatDate(f.registrationDate)}</td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {f.isActivated ? (
                          <span className="text-green-600 font-semibold">{formatDate(f.activatedAt)}</span>
                        ) : (
                          <span className="text-yellow-500 text-xs font-medium italic">En attente...</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {f.isActivated ? (
                          <span className="text-sm font-extrabold text-green-600">
                            +{f.commission.toLocaleString()} {currency}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 font-medium">
                            {f.commission.toLocaleString()} {currency} (en attente)
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={6} className="px-5 py-3.5 text-sm font-bold text-gray-700">
                    Total commissions ({filteredFilleuls.filter(f => f.isActivated).length} activé(s))
                  </td>
                  <td className="px-5 py-3.5 text-right text-sm font-extrabold text-green-600">
                    +{filteredFilleuls.filter(f => f.isActivated).reduce((s, f) => s + f.commission, 0).toLocaleString()} {currency}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Légende */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
        <p className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1.5">
          <i className="ri-information-line text-teal-500"></i>
          Légende des niveaux de parrainage
        </p>
        <div className="grid grid-cols-3 gap-3">
          {([1, 2, 3] as const).map((lvl) => {
            const c = levelColors[lvl];
            const commission = REFERRAL_COMMISSIONS[`level${lvl}` as keyof typeof REFERRAL_COMMISSIONS];
            const desc = lvl === 1
              ? 'Personnes que vous avez directement invitées'
              : lvl === 2
              ? 'Personnes invitées par vos filleuls directs'
              : 'Personnes invitées par vos filleuls de niveau 2';
            return (
              <div key={lvl} className={`rounded-xl p-3 border ${c.border} ${c.bg}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-extrabold ${c.badge}`}>N{lvl}</span>
                  <span className={`text-sm font-extrabold ${c.text}`}>{commission.toLocaleString()} {currency}</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
